import merge from 'deepmerge';
import { ref } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useProductBaseStore } from '@/features/Product/stores/productBase';
import {
  ProductComplete,
  CodebaseProductData,
  LocalisedCodebaseProductData,
} from '../types/models';
import { buildCompleteProduct, getProductValueByPath } from '../services/storeProducts';
import { codeProductData } from './rawData/codeProductData';
import { getCodeProductBlank } from '../services/productBlanks';
import { useStoreMediaLocaliseHelper } from '../composables/storeMediaLocaliseHelper';
import { useStoreContentLocaliseHelper } from '../composables/storeContentLocaliseHelper';
import { useProductBundleStore } from './productBundle';
import { createProductMapKey } from '@/composables/productHelper';
import { buildIdentity } from '../services/productIdentityBuilder';

export const useProductCompleteStore = defineStore('productCompleteStore', () => {
  const cachedDisplayProducts = ref<ProductComplete[]>([]);
  const cachedCompleteProducts = ref<ProductComplete[]>([]);
  const setCachedDisplayProducts = (displayProducts: ProductComplete[]) => {
    cachedDisplayProducts.value = displayProducts;
  };

  const addCachedCompleteProduct = (product: ProductComplete) => {
    cachedCompleteProducts.value.push(product);
    return product;
  };

  const clearCachedCompleteProducts = () => {
    cachedCompleteProducts.value = [];
  };

  const buildCompleteFromDisplay = (displayProduct: ProductComplete): ProductComplete | null => {
    /* Keep these store refs inside the builder so product + bundle dependencies are resolved lazily. */
    const productBaseStore = useProductBaseStore();
    const { completeBaseProducts } = storeToRefs(productBaseStore);

    const productBundleStore = useProductBundleStore();
    const { completeBundleProducts } = storeToRefs(productBundleStore);
    try {
      if (!displayProduct?.attributes) {
        throw new Error('Product missing attributes');
      }

      const { sku, handle } = displayProduct.attributes;
      if (!sku || !handle) {
        throw new Error(`Product missing required fields - sku: ${sku}, handle: ${handle}`);
      }

      const mapKey = createProductMapKey(sku, handle);
      const codebaseProduct: CodebaseProductData =
        codeProductData.get(mapKey) || getCodeProductBlank(sku);

      const localisedMedia = useStoreMediaLocaliseHelper(codebaseProduct.media);
      const localisedContent = useStoreContentLocaliseHelper(
        codebaseProduct.content,
        codebaseProduct.attributes.sku,
      );
      const localisedCodebaseProduct: LocalisedCodebaseProductData = {
        ...codebaseProduct,
        media: localisedMedia,
        content: localisedContent,
      };

      const mergedDisplayProduct = merge<ProductComplete>(
        displayProduct as Partial<ProductComplete>,
        localisedCodebaseProduct as Partial<ProductComplete>,
      );
      const completeProduct = buildCompleteProduct(
        mergedDisplayProduct,
        completeBaseProducts.value,
        completeBundleProducts.value,
      );
      if (completeProduct) {
        completeProduct.attributes.identity = buildIdentity(completeProduct);
      }
      return completeProduct;
    } catch (error) {
      console.error('Failed to build complete product:', {
        sku: displayProduct?.attributes?.sku,
        handle: displayProduct?.attributes?.handle,
        error: error instanceof Error ? error.message : error,
      });
      return null;
    }
  };

  const getCompleteProduct = (
    keyPath: string,
    pathValue: string | number,
  ): ProductComplete | undefined => {
    try {
      if (!keyPath || pathValue === undefined || pathValue === null) {
        throw new Error(
          `Invalid search parameters - keyPath: "${keyPath}", pathValue: "${pathValue}"`,
        );
      }

      const cachedComplete = cachedCompleteProducts.value.find(
        (product) => getProductValueByPath(product, keyPath) === pathValue,
      );
      if (cachedComplete) return cachedComplete;

      const displayProduct = cachedDisplayProducts.value.find(
        (product) => getProductValueByPath(product, keyPath) === pathValue,
      );
      if (!displayProduct) return undefined;

      const completedProduct = buildCompleteFromDisplay(displayProduct);
      if (completedProduct) {
        addCachedCompleteProduct(completedProduct);
        return completedProduct;
      }
      return undefined;
    } catch (error) {
      console.error('Failed to get complete product:', {
        keyPath,
        pathValue,
        error: error instanceof Error ? error.message : error,
      });
      return undefined;
    }
  };

  return {
    cachedDisplayProducts,
    cachedCompleteProducts,
    clearCachedCompleteProducts,
    setCachedDisplayProducts,
    getCompleteProduct,
  };
});
